// The next-up picker (docs/areas/learn.md): not a kind, a pure query the views call. Given a time budget
// it says what to do now, as a deck of cards 5 to 8 long, and no two in a row share a format.
//
// Order: due reviews first (concepts whose status.next_review_at has come, five at most, each as a
// question); then new cards from the active course's next lessons, teaching before quizzing (a concept's
// Learn card, then a question on it; the lesson card once its concepts are taught); then one combination
// when one is eligible (a concept in it is past new); then a spot card. Combination exercises are the ones
// with several `exercises` edges. A concept with no exercises of its own is shown as a plain concept card.
import type { Edge } from '@/db/types'
import type {
  ConceptResource,
  CourseResource,
  ExerciseResource,
  LessonResource,
  ModuleResource,
  SessionResource,
} from '@/kinds/learn/apply'
import type { ExerciseFormat, SessionMode } from '@/kinds/learn/schema'
import { outlineOf } from './learn'
import type { TestSetup } from './learnTest'

/** The most due reviews one deck takes. */
export const MAX_DUE = 5
/** A deck is 5 to 8 cards when there is that much to do. */
export const MIN_CARDS = 5
export const MAX_CARDS = 8
/** Minutes: one deck is about three; "keep going" is a longer one. */
export const ONE_DECK_MINUTES = 3
export const KEEP_GOING_MINUTES = 6
/** What a deck left out is not offered again for this long, so "one more deck" is a new one. */
export const SEEN_WINDOW_MS = 30 * 60 * 1000

/**
 * A card. The four exercise formats carry their exercise and the concepts it is about (a combination has
 * several); 'lesson' opens the lesson's link and marks it done; 'concept' is the fallback for a concept
 * that has no exercises yet. The generator (L4) only adds Exercises, so nothing here changes for it.
 */
type ExCard<F extends ExerciseFormat> = F extends F
  ? { format: F; id: string; exercise: ExerciseResource; concepts: ConceptResource[] }
  : never
export type ExerciseCard = ExCard<ExerciseFormat>
export type DeckCard =
  | ExerciseCard
  | { format: 'lesson'; id: string; lesson: LessonResource }
  | { format: 'concept'; id: string; concept: ConceptResource }

export const isExerciseCard = (c: DeckCard): c is ExerciseCard => 'exercise' in c

export interface Deck {
  mode: SessionMode
  cards: DeckCard[]
  /** the course the lessons came from, when any did */
  courseId?: string
  /** a test: how it was set up and where more questions come from (Endless asks for more as it goes) */
  test?: {
    setup: TestSetup
    pool: ExerciseCard[]
    draw: { next(n: number): ExerciseCard[] }
  }
}

export interface Records {
  courses: CourseResource[]
  modules: ModuleResource[]
  lessons: LessonResource[]
  concepts: ConceptResource[]
  exercises: ExerciseResource[]
  sessions: SessionResource[]
  edges: Edge[]
}

/** How many cards a time budget buys: about half a minute each, 5 to 8. */
export const cardsFor = (minutes: number): number =>
  Math.min(MAX_CARDS, Math.max(MIN_CARDS, Math.round(minutes * 2)))

const live = <R extends { deleted_at: string | null }>(rows: R[]) => rows.filter((r) => !r.deleted_at)

/** Ids covered by a session that started within the window: seen a moment ago. */
export const recentlyCovered = (
  sessions: SessionResource[],
  edges: Edge[],
  now: Date,
  windowMs: number = SEEN_WINDOW_MS,
): Set<string> => {
  const recent = new Set(
    live(sessions)
      .filter((s) => now.getTime() - new Date(s.spec.started_at).getTime() < windowMs)
      .map((s) => s.id),
  )
  return new Set(
    edges
      .filter((e) => !e.deleted_at && e.relation === 'covers' && recent.has(e.from_id))
      .map((e) => e.to_id),
  )
}

/** Concepts due for review at `now`, most overdue first, at most `MAX_DUE`. */
export const dueConcepts = (concepts: ConceptResource[], now: Date): ConceptResource[] =>
  live(concepts)
    .filter((c) => c.status.next_review_at && new Date(c.status.next_review_at) <= now)
    .sort((a, b) => a.status.next_review_at!.localeCompare(b.status.next_review_at!))
    .slice(0, MAX_DUE)

/** The course in hand: the one with the latest session, else the newest; done courses are not. */
export const activeCourse = (courses: CourseResource[]): CourseResource | undefined =>
  live(courses)
    .filter((c) => c.status.state !== 'done')
    .sort(
      (a, b) =>
        (b.status.last_session_at ?? '').localeCompare(a.status.last_session_at ?? '') ||
        a.created_at.localeCompare(b.created_at),
    )[0]

const answeredAt = (x: ExerciseResource) => x.status.answered_at ?? ''

/** Never answered first, then the one answered longest ago; a Learn card by its order. */
const freshest = (a: ExerciseResource, b: ExerciseResource) =>
  answeredAt(a).localeCompare(answeredAt(b)) ||
  (a.spec.order ?? 0) - (b.spec.order ?? 0) ||
  a.id.localeCompare(b.id)

/** Whether a concept is past new: it has been marked so, or something on it has been answered. */
export const pastNew = (c: ConceptResource, exercises: ExerciseResource[]) =>
  c.status.familiarity !== 'new' || exercises.some((x) => x.status.state === 'answered')

export const nextUp = (r: Records, minutes: number, now: Date): Deck => {
  const want = cardsFor(minutes)
  const seen = recentlyCovered(r.sessions, r.edges, now)
  const concepts = new Map(live(r.concepts).map((c) => [c.id, c]))
  const exercises = live(r.exercises).filter((x) => !seen.has(x.id))
  const allExercises = live(r.exercises)
  // exercise <-> concepts through `exercises` edges
  const conceptsOf = new Map<string, ConceptResource[]>()
  const exercisesOf = new Map<string, ExerciseResource[]>()
  const byId = new Map(allExercises.map((x) => [x.id, x]))
  for (const e of r.edges) {
    if (e.deleted_at || e.relation !== 'exercises') continue
    const x = byId.get(e.from_id)
    const c = concepts.get(e.to_id)
    if (!x || !c) continue
    conceptsOf.set(x.id, [...(conceptsOf.get(x.id) ?? []), c])
    exercisesOf.set(c.id, [...(exercisesOf.get(c.id) ?? []), x])
  }
  const single = (x: ExerciseResource) => (conceptsOf.get(x.id)?.length ?? 0) === 1
  const own = (c: ConceptResource) => (exercisesOf.get(c.id) ?? []).filter(single)
  const fresh = new Set(exercises.map((x) => x.id))

  const cards: DeckCard[] = []
  const used = new Set<string>()
  const prev = (): ExerciseFormat | undefined => {
    const p = cards[cards.length - 1]
    return p && isExerciseCard(p) ? p.format : undefined
  }
  const push = (x: ExerciseResource) => {
    used.add(x.id)
    cards.push({
      format: x.spec.format,
      id: x.id,
      exercise: x,
      concepts: conceptsOf.get(x.id) ?? [],
    } as ExerciseCard)
  }
  const ok = (x: ExerciseResource) => fresh.has(x.id) && !used.has(x.id)
  /** the next Learn card of a concept not yet read */
  const nextLearn = (c: ConceptResource) =>
    own(c)
      .filter((x) => x.spec.format === 'learn' && x.status.state === 'open' && ok(x))
      .sort(freshest)[0]
  /** a question on the concept, one that does not repeat the format just shown */
  const quiz = (c: ConceptResource, not?: ExerciseFormat) => {
    const all = own(c)
      .filter((x) => x.spec.format !== 'learn' && ok(x))
      .sort(freshest)
    return all.find((x) => x.spec.format !== not) ?? all[0]
  }
  const reviewCard = (c: ConceptResource) => {
    const x = quiz(c, prev())
    if (x) return push(x)
    const l = own(c)
      .filter((y) => y.spec.format === 'learn' && ok(y))
      .sort(freshest)[0]
    if (l) return push(l)
    if (!(exercisesOf.get(c.id) ?? []).length) {
      used.add(c.id)
      cards.push({ format: 'concept', id: c.id, concept: c })
    }
  }

  // reserve room for a combination and a closing spot card
  const combo = comboCard(allExercises, conceptsOf, exercisesOf, ok, fresh)
  const reserve = (combo ? 1 : 0) + 1
  const room = Math.max(2, want - reserve)

  // 1. reviews that are due
  const due = dueConcepts(r.concepts, now).filter((c) => !seen.has(c.id))
  for (const c of due) {
    if (cards.length >= Math.min(room, MAX_DUE)) break
    reviewCard(c)
  }
  const dueCount = cards.length

  // 2. new cards from the next lessons: teach, then quiz; the lesson card once taught
  const course = activeCourse(r.courses)
  const inDeck = new Set(due.map((c) => c.id))
  let lessonsUsed = false
  if (course) {
    for (const m of outlineOf(course.id, r)) {
      for (const { lesson, concepts: taught } of m.lessons) {
        if (cards.length >= room) break
        if (lesson.status.state !== 'todo' || seen.has(lesson.id)) continue
        const todo = taught.filter((c) => c.status.familiarity !== 'known' && !inDeck.has(c.id))
        const startedAt = cards.length
        const unread = todo.filter((c) => nextLearn(c))
        for (const c of todo) {
          if (cards.length >= room) break
          const l = nextLearn(c)
          if (l) push(l)
          const q = quiz(c, prev())
          if (q && cards.length < room) push(q)
          else if (!l && !q && !(exercisesOf.get(c.id) ?? []).length && !seen.has(c.id)) {
            used.add(c.id)
            cards.push({ format: 'concept', id: c.id, concept: c })
          }
          inDeck.add(c.id)
        }
        // taught already (nothing left to read before this deck): the lesson card closes it
        if (unread.length === 0 && cards.length < room && !used.has(lesson.id)) {
          used.add(lesson.id)
          cards.push({ format: 'lesson', id: lesson.id, lesson })
          lessonsUsed = true
        }
        if (cards.length === startedAt) continue
      }
    }
  }

  // 3. a combination, once one of its concepts is past new
  if (combo && cards.length > 0 && cards.length < MAX_CARDS) push(combo)

  // 4. a spot card to finish, on a concept already in the deck or one being learned
  if (cards.length > 0 && cards.length < MAX_CARDS) {
    const pool = [
      ...cards.flatMap((c) => (isExerciseCard(c) ? c.concepts : 'concept' in c ? [c.concept] : [])),
      ...live(r.concepts).filter((c) => c.status.familiarity === 'learning'),
    ]
    const spots = pool
      .flatMap((c) => own(c))
      .filter((x) => x.spec.format === 'spot' && ok(x))
      .sort(freshest)
    const s = spots[0]
    if (s && prev() !== 'spot') push(s)
  }

  separate(cards)
  if (cards.length) {
    return {
      mode: dueCount > 0 ? 'review' : 'lesson',
      cards,
      courseId: lessonsUsed ? course?.id : undefined,
    }
  }
  return { mode: 'spot', cards: [] }
}

/** One combination exercise, if any is eligible: its Learn card first, then its questions. */
const comboCard = (
  all: ExerciseResource[],
  conceptsOf: Map<string, ConceptResource[]>,
  exercisesOf: Map<string, ExerciseResource[]>,
  ok: (x: ExerciseResource) => boolean,
  fresh: Set<string>,
): ExerciseResource | undefined => {
  const eligible = all.filter((x) => {
    const cs = conceptsOf.get(x.id) ?? []
    return (
      cs.length > 1 && ok(x) && fresh.has(x.id) && cs.some((c) => pastNew(c, exercisesOf.get(c.id) ?? []))
    )
  })
  const learn = eligible.filter((x) => x.spec.format === 'learn' && x.status.state === 'open')
  const pool = learn.length ? learn : eligible.filter((x) => x.spec.format !== 'learn')
  return pool.sort(freshest)[0]
}

/**
 * Moves cards so no two in a row share a format where it can be done without putting a question before
 * the Learn card of the same concept, or a lesson before the cards that taught it.
 */
const separate = (cards: DeckCard[]) => {
  const concernedWith = (c: DeckCard) =>
    isExerciseCard(c) ? c.concepts.map((x) => x.id) : c.format === 'concept' ? [c.concept.id] : []
  for (let i = 1; i < cards.length; i++) {
    if (cards[i]!.format !== cards[i - 1]!.format) continue
    for (let j = i + 1; j < cards.length; j++) {
      const m = cards[j]!
      if (m.format === cards[i - 1]!.format || m.format === 'lesson') continue
      // moving m up past the cards between i and j: none of them may be a Learn card of m's concepts
      const mine = new Set(concernedWith(m))
      const jumped = cards.slice(i, j)
      if (jumped.some((c) => c.format === 'learn' && concernedWith(c).some((id) => mine.has(id)))) continue
      cards.splice(i, 0, ...cards.splice(j, 1))
      break
    }
  }
}

/** Whether a deck was finished on the local calendar day of `now`: today's deck is done. */
export const deckDoneToday = (sessions: SessionResource[], now: Date): boolean => {
  const day = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  return live(sessions).some((s) => s.status.ended_at && day(new Date(s.status.ended_at)) === day(now))
}
